interface Client {
  property: string;
}

interface Props {
  client: Client;
}

const propertyDetails = {
  address: '1 Harbour View Drive, Penthouse 42, Miami, FL 33131',
  type: 'Luxury Penthouse',
  bedrooms: 4,
  bathrooms: 4.5,
  sqft: '4,820 sq ft',
  floor: '42nd Floor',
  parking: '2 Reserved Spaces',
  amenities: ['Concierge Service', 'Private Rooftop Terrace', 'Smart Home System', 'Wine Cellar', 'Home Theatre', 'Gym Access', 'Pool & Spa', 'Valet Parking'],
  currentTenant: {
    name: 'Confidential',
    leaseStart: 'January 1, 2025',
    leaseEnd: 'December 31, 2025',
    monthlyRent: '$8,500',
    securityDeposit: '$17,000',
    status: 'Active',
  },
  manager: {
    name: 'Alexandra Moore',
    role: 'Senior Relationship Manager',
    email: 'a.moore@higherstandardglobal.com',
    phone: '+1 (305) 555-0192',
  },
};

export default function PropertyTab({ client }: Props) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Property Details
        </h2>
        <p className="text-sm text-[#6B6B6B] font-light mt-1">{client.property}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Property Image + Address */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="bg-white border border-[#E8E6E3] rounded-lg overflow-hidden">
            <div className="w-full h-64 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/0d0af85cadc4471715a08f165af4d6dd.jpg"
                alt="Property"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A1A1A]">{client.property}</h3>
                  <p className="text-sm text-[#6B6B6B] font-light mt-1">{propertyDetails.address}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs px-3 py-1.5 rounded-full font-medium">
                  <i className="ri-checkbox-circle-line"></i>
                  {propertyDetails.currentTenant.status}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Type', value: propertyDetails.type, icon: 'ri-building-line' },
                  { label: 'Bedrooms', value: `${propertyDetails.bedrooms} BR`, icon: 'ri-hotel-bed-line' },
                  { label: 'Bathrooms', value: `${propertyDetails.bathrooms} BA`, icon: 'ri-drop-line' },
                  { label: 'Size', value: propertyDetails.sqft, icon: 'ri-ruler-line' },
                ].map((item) => (
                  <div key={item.label} className="flex flex-col gap-1.5 p-3 bg-[#FAFAF8] rounded-md">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-[#8B7355] text-sm`}></i>
                    </div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{item.value}</p>
                    <p className="text-xs text-[#9A9A9A]">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="bg-white border border-[#E8E6E3] rounded-lg p-6">
            <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-4">Amenities & Features</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {propertyDetails.amenities.map((amenity) => (
                <div key={amenity} className="flex items-center gap-2 text-sm text-[#2C2C2C]">
                  <i className="ri-checkbox-circle-fill text-[#8B7355] text-sm flex-shrink-0"></i>
                  <span className="text-xs font-light">{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          {/* Tenancy Info */}
          <div className="bg-white border border-[#E8E6E3] rounded-lg p-6">
            <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-4">Current Tenancy</h3>
            <div className="flex flex-col gap-3">
              {[
                { label: 'Tenant', value: propertyDetails.currentTenant.name },
                { label: 'Lease Start', value: propertyDetails.currentTenant.leaseStart },
                { label: 'Lease End', value: propertyDetails.currentTenant.leaseEnd },
                { label: 'Monthly Rent', value: propertyDetails.currentTenant.monthlyRent },
                { label: 'Security Deposit', value: propertyDetails.currentTenant.securityDeposit },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-center py-2 border-b border-[#F0EDE9] last:border-0">
                  <span className="text-xs text-[#9A9A9A]">{item.label}</span>
                  <span className="text-xs font-medium text-[#1A1A1A]">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Manager */}
          <div className="bg-[#2C2C2C] rounded-lg p-6 text-white">
            <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-white/60 mb-4">Your Manager</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#8B7355] text-white text-sm font-semibold flex-shrink-0">
                AM
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{propertyDetails.manager.name}</p>
                <p className="text-xs text-white/50 font-light">{propertyDetails.manager.role}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <a href={`mailto:${propertyDetails.manager.email}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-mail-line text-[#8B7355]"></i>
                {propertyDetails.manager.email}
              </a>
              <a href={`tel:${propertyDetails.manager.phone}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors cursor-pointer">
                <i className="ri-phone-line text-[#8B7355]"></i>
                {propertyDetails.manager.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
